/**
 * Backstage — shared helpers for the mobile talent-portal redesign.
 *
 * "Backstage" is the mobile-only luxury skin for the Talent Portal. These
 * helpers are intentionally tiny and presentation-only; all data wiring stays
 * in the components that import them.
 */
import { format, parseISO } from "date-fns";
import { parseEventDate } from "@shared/event-date";

/** Two-letter initials from a person / talent name. */
export function initials(name?: string | null): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Deterministic, brand-tinted avatar swatch (bg + text tailwind classes).
 * Warm, quiet tones drawn from the Clarik palette — never loud.
 */
const AVATAR_SWATCHES = [
  "bg-primary/12 text-primary",
  "bg-foreground/[0.07] text-foreground",
  "bg-[hsl(var(--graphite)/0.15)] text-[hsl(var(--graphite))]",
  "bg-[hsl(var(--green-soft))] text-primary",
  "bg-[hsl(var(--ink-soft)/0.10)] text-foreground",
];

export function avatarSwatch(seed?: string | null): string {
  const s = seed || "";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_SWATCHES[h % AVATAR_SWATCHES.length];
}

/** Friendly greeting based on the local hour. */
export function greeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 5) return "Up late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * Human countdown for a calendar-day event string (YYYY-MM-DD) or instant.
 * Returns e.g. "Today", "Tomorrow", "In 3 days", "In 2 weeks", or "" for past.
 */
export function countdownLabel(value?: string | Date | null): string {
  const target = toDate(value);
  if (!target) return "";
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(new Date());
  const ev = startOfDay(target);
  const diffDays = Math.round((ev.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0) return "";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < 14) return "Next week";
  if (diffDays < 30) return `In ${Math.round(diffDays / 7)} weeks`;
  if (diffDays < 60) return "In a month";
  return `In ${Math.round(diffDays / 30)} months`;
}

function toDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  try {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return parseEventDate(value) ?? parseISO(value);
    }
    return typeof value === "string" ? parseISO(value) : value;
  } catch {
    return null;
  }
}

/** "Sat, Jun 14" style date. Date-only strings never drift across timezones. */
export function longDate(value?: string | Date | null): string {
  const d = toDate(value);
  if (!d) return "Date TBD";
  return format(d, "EEEE, MMMM d");
}

export function shortDate(value?: string | Date | null): string {
  const d = toDate(value);
  if (!d) return "TBD";
  return format(d, "EEE, MMM d");
}

/** First chunk of an address — the venue/city line. */
export function placeLine(address?: string | null): string {
  if (!address) return "";
  return address.split(",").slice(0, 2).join(", ").trim();
}

export function directionsUrl(address?: string | null): string | null {
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/** Build a downloadable vCard (save-to-phone) for a contact. */
export function contactVCard(c: {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  notes?: string | null;
}): string {
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${(c.name || "").replace(/\n/g, " ")}`,
  ];
  if (c.company) lines.push(`ORG:${c.company.replace(/\n/g, " ")}`);
  if (c.phone) lines.push(`TEL;TYPE=CELL:${c.phone}`);
  if (c.email) lines.push(`EMAIL:${c.email}`);
  if (c.notes) lines.push(`NOTE:${c.notes.replace(/\n/g, " ")}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

export const CONTACT_ROLES: Array<{ value: string; label: string }> = [
  { value: "tour_manager", label: "Tour Manager" },
  { value: "sound_tech", label: "Sound Tech" },
  { value: "driver", label: "Driver" },
  { value: "venue_mgr", label: "Venue Manager" },
  { value: "promoter_contact", label: "Promoter" },
  { value: "hair_makeup", label: "Hair / Makeup" },
  { value: "hotel", label: "Hotel" },
  { value: "opener", label: "Opener" },
  { value: "merch", label: "Merch" },
  { value: "photographer", label: "Photographer" },
  { value: "caterer", label: "Caterer" },
  { value: "security", label: "Security" },
  { value: "other", label: "Other" },
];

export function roleLabel(role?: string | null): string {
  return CONTACT_ROLES.find((r) => r.value === role)?.label || "Contact";
}
